import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabaseWorkoutRepository } from '../infrastructure/supabase-workout.repository';
import { enqueueSessionOp, enqueueSetUpdate } from '../infrastructure/dexie-queue';
import { isLikelyNetworkError } from '../infrastructure/sync-worker';
import { type SetDefaults } from './workout.repository';
import {
  type SessionExerciseGroup,
  type SetPatch,
  type StartAdHocInput,
  type StartBackfillInput,
  type StartFromProgramDayInput,
  type WorkoutSession,
  type WorkoutSessionFull,
  type WorkoutSet,
} from '../domain/workout';

const repo = supabaseWorkoutRepository;

const workoutsKey = ['workouts'] as const;
const fullKey = (id: string) => ['workouts', 'full', id] as const;

export function useMyWorkouts(): ReturnType<typeof useQuery<WorkoutSession[]>> {
  return useQuery({
    queryKey: [...workoutsKey, 'mine'],
    queryFn: () => repo.listMine(),
  });
}

export function useWorkoutsForUser(
  profileId: string,
): ReturnType<typeof useQuery<WorkoutSession[]>> {
  return useQuery({
    queryKey: [...workoutsKey, 'forUser', profileId],
    queryFn: () => repo.listForUser(profileId),
  });
}

export function useActiveWorkout(): ReturnType<typeof useQuery<WorkoutSession | null>> {
  return useQuery({
    queryKey: [...workoutsKey, 'active'],
    queryFn: () => repo.getActive(),
  });
}

export function useWorkoutFull(id: string): ReturnType<typeof useQuery<WorkoutSessionFull>> {
  return useQuery({
    queryKey: fullKey(id),
    queryFn: () => repo.getFull(id),
    refetchOnMount: 'always',
  });
}

// --------- mutations ---------

export function useStartAdHoc(): ReturnType<
  typeof useMutation<WorkoutSession, Error, StartAdHocInput>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StartAdHocInput) => repo.startAdHoc(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: workoutsKey });
    },
  });
}

export function useStartFromProgramDay(): ReturnType<
  typeof useMutation<WorkoutSession, Error, StartFromProgramDayInput>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StartFromProgramDayInput) => repo.startFromProgramDay(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: workoutsKey });
    },
  });
}

export function useStartBackfill(): ReturnType<
  typeof useMutation<WorkoutSession, Error, StartBackfillInput>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StartBackfillInput) => repo.startBackfill(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: workoutsKey });
    },
  });
}

export function useCopyWorkout(): ReturnType<typeof useMutation<WorkoutSession, Error, string>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sourceSessionId: string) => repo.copyFromSession(sourceSessionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: workoutsKey });
    },
  });
}

interface SetSessionDateInput {
  readonly sessionId: string;
  readonly newDate: string;
}

export function useSetSessionDate(): ReturnType<
  typeof useMutation<WorkoutSession, Error, SetSessionDateInput>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetSessionDateInput) => repo.setSessionDate(input.sessionId, input.newDate),
    onSuccess: (session) => {
      void qc.invalidateQueries({ queryKey: workoutsKey });
      void qc.invalidateQueries({ queryKey: fullKey(session.id) });
      // Progress history (recent sessions) needs to refresh too
      void qc.invalidateQueries({ queryKey: ['progress'] });
    },
  });
}

interface FinishInput {
  readonly sessionId: string;
  /** Override timestamp — used by backfill to land on the past day. */
  readonly completedAt?: string;
}

export function useFinishWorkout(): ReturnType<
  typeof useMutation<WorkoutSession | null, Error, FinishInput | string>
> {
  const qc = useQueryClient();
  return useMutation({
    // Offline-tolerant: if the round-trip fails on a likely network error,
    // we (a) optimistically patch the cached session as completed and (b)
    // enqueue a session op so the sync worker finalises later. Returning
    // null signals "queued, no fresh row available"; callers don't depend
    // on the response shape (handleNextExercise just await-skips).
    mutationFn: async (input: FinishInput | string) => {
      const sessionId = typeof input === 'string' ? input : input.sessionId;
      const completedAt = typeof input === 'string' ? undefined : input.completedAt;
      try {
        return completedAt === undefined
          ? await repo.finish(sessionId)
          : await repo.finish(sessionId, completedAt);
      } catch (e) {
        if (isLikelyNetworkError(e)) {
          // Capture the click moment, not the eventual flush time — so a
          // lifter who finishes at 18:00 and reconnects at 19:30 still has
          // an 18:00-stamped session.
          await enqueueSessionOp(sessionId, 'finish', completedAt ?? new Date().toISOString(), e);
          return null;
        }
        throw e;
      }
    },
    onMutate: (input: FinishInput | string) => {
      // Optimistic patch the active session list + full cache so the UI
      // flips to "finished" instantly. Server reconciles on next refetch
      // (success) or the queued session op (offline).
      const sessionId = typeof input === 'string' ? input : input.sessionId;
      const stampedAt =
        typeof input === 'string'
          ? new Date().toISOString()
          : (input.completedAt ?? new Date().toISOString());
      const full = qc.getQueryData<WorkoutSessionFull>(fullKey(sessionId));
      if (full && !full.completedAt) {
        qc.setQueryData<WorkoutSessionFull>(fullKey(sessionId), {
          ...full,
          completedAt: stampedAt,
        });
      }
    },
    onSettled: (_session, _err, input) => {
      const sessionId = typeof input === 'string' ? input : input.sessionId;
      void qc.invalidateQueries({ queryKey: workoutsKey });
      void qc.invalidateQueries({ queryKey: fullKey(sessionId) });
    },
  });
}

export function useCancelWorkout(): ReturnType<typeof useMutation<void, Error, string>> {
  const qc = useQueryClient();
  return useMutation({
    // Offline-tolerant: on network error, queue the cancel and remove the
    // local cache so the UI behaves as if the session is gone. Sync worker
    // replays the delete when connectivity returns; a "not found" later is
    // treated as success because the user's intent (be gone) is satisfied.
    mutationFn: async (sessionId: string) => {
      try {
        await repo.cancel(sessionId);
      } catch (e) {
        if (isLikelyNetworkError(e)) {
          await enqueueSessionOp(sessionId, 'cancel', null, e);
          return;
        }
        throw e;
      }
    },
    onSuccess: (_, sessionId) => {
      void qc.invalidateQueries({ queryKey: workoutsKey });
      // Session row is (going to be) gone; drop the cached full-detail
      // query so a stale navigation back to /workout/$id doesn't refetch
      // a deleted row.
      qc.removeQueries({ queryKey: fullKey(sessionId) });
    },
  });
}

interface AddExerciseToWorkoutVars {
  readonly exerciseId: string;
  readonly setsCount: number;
  readonly defaults?: SetDefaults;
}

export function useAddExerciseToWorkout(
  sessionId: string,
): ReturnType<typeof useMutation<WorkoutSet, Error, AddExerciseToWorkoutVars>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ exerciseId, setsCount, defaults }: AddExerciseToWorkoutVars) =>
      repo.addExercise(sessionId, exerciseId, setsCount, defaults),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: fullKey(sessionId) });
    },
  });
}

/**
 * Lookup the user's most recent completed set for an exercise — used by the
 * ad-hoc add dialog to pre-fill weight/reps/duration/distance defaults so
 * the user starts from "what I did last time". Returns null when there's no
 * history. Cached short-term (1 min) to avoid hammering on each dialog open.
 */
export function useLastCompletedSet(
  exerciseId: string | null,
): ReturnType<typeof useQuery<WorkoutSet | null>> {
  return useQuery({
    queryKey: ['workouts', 'last-completed-set', exerciseId ?? ''],
    queryFn: () => (exerciseId ? repo.getLastCompletedSet(exerciseId) : Promise.resolve(null)),
    enabled: !!exerciseId,
    staleTime: 60_000,
  });
}

export function useAddSet(
  sessionId: string,
): ReturnType<typeof useMutation<WorkoutSet, Error, string>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (exerciseId: string) => repo.addSet(sessionId, exerciseId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: fullKey(sessionId) });
    },
  });
}

/**
 * Offline-aware update with optimistic cache patching.
 *
 * Behaviour:
 *  - onMutate: patch the cached WorkoutSessionFull immediately so the UI
 *    reflects the change with zero latency.
 *  - mutationFn: try the network; on a likely network error, enqueue for
 *    later flush and resolve with `null` (no rollback).
 *  - onError (non-network): roll the cache back to the snapshot.
 *  - onSettled: revalidate to reconcile with the server.
 */
export function useUpdateSet(
  sessionId: string,
): ReturnType<
  typeof useMutation<
    WorkoutSet | null,
    Error,
    { setId: string; patch: SetPatch },
    { previous: WorkoutSessionFull | undefined }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ setId, patch }) => {
      try {
        return await repo.updateSet(setId, patch);
      } catch (e) {
        if (isLikelyNetworkError(e)) {
          await enqueueSetUpdate(setId, patch, e);
          return null; // optimistic success — queue will retry
        }
        throw e;
      }
    },
    onMutate: async ({ setId, patch }) => {
      const key = fullKey(sessionId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<WorkoutSessionFull>(key);
      if (previous) {
        qc.setQueryData<WorkoutSessionFull>(key, applyOptimisticPatch(previous, setId, patch));
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(fullKey(sessionId), ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: fullKey(sessionId) });
    },
  });
}

function applyOptimisticPatch(
  session: WorkoutSessionFull,
  setId: string,
  patch: SetPatch,
): WorkoutSessionFull {
  const groups = session.groups.map<SessionExerciseGroup>((g) => {
    const sets = g.sets.map((s) => {
      if (s.id !== setId) return s;
      const next: WorkoutSet = {
        ...s,
        ...(patch.weight !== undefined && { weight: patch.weight }),
        ...(patch.reps !== undefined && { reps: patch.reps }),
        ...(patch.durationSeconds !== undefined && { durationSeconds: patch.durationSeconds }),
        ...(patch.distanceMeters !== undefined && { distanceMeters: patch.distanceMeters }),
        ...(patch.rpe !== undefined && { rpe: patch.rpe }),
        ...(patch.completed !== undefined && {
          completed: patch.completed,
          completedAt: patch.completed ? new Date().toISOString() : null,
        }),
      };
      return next;
    });
    return { ...g, sets };
  });
  return { ...session, groups };
}

export function useDeleteSet(
  sessionId: string,
): ReturnType<typeof useMutation<void, Error, string>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (setId: string) => repo.deleteSet(setId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: fullKey(sessionId) });
    },
  });
}

/**
 * Stamps started_at = now() on the given set. Used by guided mode when the
 * user begins a set (and again if they re-enter that set after a back-button
 * — last-write-wins is fine).
 *
 * Optimistic: patches the cached set immediately so the live timer can read
 * its anchor without waiting for the round trip.
 */
export function useStartSet(
  sessionId: string,
): ReturnType<
  typeof useMutation<WorkoutSet | null, Error, string, { previous: WorkoutSessionFull | undefined }>
> {
  const qc = useQueryClient();
  return useMutation({
    // Offline-aware: if the round-trip fails on the network, swallow the
    // error and let the optimistic patch in onMutate be the source of truth.
    // startSet is idempotent (last write wins on started_at) so retrying it
    // later is safe — and there's nothing to queue for, since the next
    // updateSet on this set will rewrite started_at anyway when the lifter
    // hits Готово. Phase transitions in guided mode wait on this mutation;
    // if we let it throw, the lifter is stuck on rest forever offline.
    mutationFn: async (setId: string) => {
      try {
        return await repo.startSet(setId);
      } catch (e) {
        if (isLikelyNetworkError(e)) return null;
        throw e;
      }
    },
    onMutate: async (setId) => {
      const key = fullKey(sessionId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<WorkoutSessionFull>(key);
      if (previous) {
        const nowIso = new Date().toISOString();
        const groups = previous.groups.map((g) => ({
          ...g,
          sets: g.sets.map((s) => (s.id === setId ? { ...s, startedAt: nowIso } : s)),
        }));
        qc.setQueryData<WorkoutSessionFull>(key, { ...previous, groups });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(fullKey(sessionId), ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: fullKey(sessionId) });
    },
  });
}
